import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HvTabs, HvTab } from '@hitachivantara/uikit-react-core';
import { Button } from '@iq/react-components';

import TypeLabel from '../../TypeLabel';
import Heading from '../../Heading';
import ConfirmationDialog from '../../ConfirmationDialog/ConfirmationDialog';
import { getActiveSite } from '../../../bundles/sites';
import {
  getRoleNotificationConfigs,
  getUserNotificationConfigs,
  resetAllNotificationConfigs,
  requestUserNotificationConfigs,
} from '../../../bundles/notification-config';
import { requestMembers, getMembers } from '../../../bundles/auth';
import { requestUsers, getUsersLoading } from '../../../bundles/ad';

import RolesTab from './components/RolesTab';
import UsersTab from './components/UsersTab';
import { isConfigCustom } from './constants';

import './NotificationView.scss';

const TransformerAlarmsView = () => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const roleConfigs = useSelector(getRoleNotificationConfigs);
  const userConfigs = useSelector(getUserNotificationConfigs);
  const siteMembers = useSelector((state) => getMembers(state, `site/${site?.id}`));
  const loadingUsers = useSelector(getUsersLoading);
  const [tabIndex, setTabIndex] = useState(0);
  const [showResetAll, setShowResetAll] = useState(false);

  useEffect(() => {
    if (site?.id) {
      dispatch(requestUsers());
      dispatch(requestUserNotificationConfigs({ org: site.org, site: site.id }));
    }
  }, [dispatch, site?.id, site?.org]);

  useEffect(() => {
    if (!loadingUsers && site?.id) {
      dispatch(requestMembers(`site/${site.id}`));
    }
  }, [dispatch, loadingUsers, site?.id]);

  const { customRoles, customUsers } = useMemo(() => {
    if (siteMembers.length === 0) return { customRoles: 0, customUsers: 0 };
    const memberMap = new Map(siteMembers.map((m) => [m.id, m.type]));
    const customRoleCount = roleConfigs.filter((c) =>
      isConfigCustom(memberMap.get(c.role) || c.role, c)
    ).length;
    return { customRoles: customRoleCount, customUsers: userConfigs.length };
  }, [roleConfigs, userConfigs, siteMembers]);

  const handleChange = (_event, newTabIndex) => setTabIndex(newTabIndex);

  const handleResetAll = () => {
    dispatch(resetAllNotificationConfigs({ org: site.org, site: site.id }));
    setShowResetAll(false);
  };

  return (
    <div className="transformer-alarms-view">
      <Heading
        contentLeft={
          <div className="transformer-alarms-view__heading">
            <div className="title">Transformer Alarms</div>
            <div className="transformer-alarms-view__tags">
              {customRoles > 0 && (
                <TypeLabel
                  text={`${customRoles} custom role${customRoles !== 1 ? 's' : ''}`}
                  color="grey"
                />
              )}
              {customUsers > 0 && (
                <TypeLabel
                  text={`${customUsers} custom user${customUsers !== 1 ? 's' : ''}`}
                  color="grey"
                />
              )}
            </div>
          </div>
        }
        contentRight={
          <Button
            activity="secondary"
            onClick={() => setShowResetAll(true)}
            disabled={customRoles === 0 && customUsers === 0}
          >
            Reset all to default
          </Button>
        }
      />
      <div className="transformer-alarms-view__panel">
        <HvTabs
          id="notification-tabs"
          value={tabIndex}
          onChange={handleChange}
        >
          <HvTab
            id="roles"
            label="Roles"
          />
          <HvTab
            id="users"
            label="Users"
          />
        </HvTabs>
        <div className="transformer-alarms-view__content">
          {tabIndex === 0 && <RolesTab />}
          {tabIndex === 1 && <UsersTab />}
        </div>
      </div>

      {showResetAll && (
        <ConfirmationDialog
          title="Reset all settings & remove customizations"
          body={
            <>
              <p>
                Returns all settings to its original defaults. All role-based and per-user
                configurations will be permanently erased. Start from a completely clean slate.
              </p>
            </>
          }
          confirmText="Reset All"
          confirmType="danger"
          showCancel
          onConfirm={handleResetAll}
          onCancel={() => setShowResetAll(false)}
          className="size-s"
        />
      )}
    </div>
  );
};

export default TransformerAlarmsView;
